import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { DIALOGS } from 'src/models/DialogModel';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel,
  EXTENSION,
  FORMAT,
  ORIENTATION,
} from 'src/models/DownloadModel';
import { useState } from 'react';
import { closeDialog } from 'src/redux/actions/dialogActions';
import { postDownloadEntitesData, postEmailEntitiesData } from 'src/apis/QuerybuilderAPI';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { PRESET_TYPES } from 'src/models/PresetModel';
import { f, t } from 'src/lib/language';
import { DownloadOption } from 'src/lib/makeApiRequest';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { makeStyles } from '@material-ui/core/styles';
import { FORM } from 'src/constants/Form';

interface DashboardDownloadPayload {
  entity: ENTITIES;
  filters: Record<string, unknown>;
  defaultFileName: string;
}

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
}));

const DashboardDownloadDialog = () => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const organisation = useSelector((state: Store) => state.currentOrganisation.id);
  const customfields = useSelector((state: Store) => state.customfields);

  const opened = useSelector(
    (state: Store) => state.dialog[DIALOGS.dashboardDownload]?.opened || false,
  );
  const payload: DashboardDownloadPayload = useSelector(
    (state: Store) => state.dialog[DIALOGS.dashboardDownload]?.payload || {},
  );

  const { entity, filters, defaultFileName } = payload;

  const defaultLayoutColumns = useSelector((state: Store) => {
    if (!entity || !state.presets[entity]) return null;
    const layoutPresets = state.presets[entity][PRESET_TYPES.layout];
    if (!layoutPresets) return null;
    const defaultPreset = layoutPresets.find((preset) => preset.is_default);
    return defaultPreset?.filter ?? null;
  });

  const [options, setOptions] = useState<DownloadOptionsModel>({
    file: defaultFileName || 'download',
    orientation: ORIENTATION.landscape,
    format: FORMAT.csv,
    delivery: DELIVERY_TYPE.download,
    export_to_grid: true,
  });

  const extension = options.format === FORMAT.csv ? EXTENSION.csv : EXTENSION.pdf;

  const getFieldsFromLayout = (): string[] => {
    if (!defaultLayoutColumns || !Array.isArray(defaultLayoutColumns)) return [];
    return defaultLayoutColumns.reduce((result: string[], column: any) => {
      if (column.hide) return result;
      const [table, property] = String(column.field || '').split('.');
      const caption = table === 'custom_fields'
        ? customfields[property]?.[`display_${lang}`]
        : f(lang, column.field);
      result.push(`${column.field}@${caption}`);
      return result;
    }, []);
  };

  const handleCancel = () => {
    dispatch(closeDialog({ dialog: DIALOGS.dashboardDownload }));
  };

  const handleDownload = async () => {
    if (!entity || !filters) return;

    const fileBase = (options.file || defaultFileName || 'download').split('.')[0];
    const downloadOptions: DownloadOptionsModel = {
      ...options,
      file: `${fileBase}.${extension}`,
    };

    const fields = getFieldsFromLayout();
    const query: Record<string, unknown> = {
      filters,
      options: downloadOptions,
    };
    if (fields.length > 0) {
      query.fields = fields;
    }

    dispatch(closeDialog({ dialog: DIALOGS.dashboardDownload }));

    try {
      if (downloadOptions.delivery === DELIVERY_TYPE.download) {
        await postDownloadEntitesData(
          organisation,
          entity,
          query as any,
          downloadOptions,
          DownloadOption.download,
        );
      } else {
        await postEmailEntitiesData(
          organisation,
          entity,
          query as any,
          downloadOptions,
        );
      }
    } catch {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.download_error',
      }));
    }
  };

  const onChange = (value: string, field: string) => {
    setOptions({
      ...options,
      [field]: value,
    });
  };

  return (
    <Dialog
      aria-labelledby="dashboard-download"
      open={opened}>
      <DialogTitle id="dashboard-download">{t(lang, 'menus.download_result')}</DialogTitle>
      <DialogContent dividers>
        <div style={{ width: '480px' }}>
          <div className={classes.row}>
            <FormTextField
              form={FORM.download}
              label="File Name"
              name="file"
              onChange={onChange as any}
              required
              value={(options.file || '').split('.')[0]} />
            {`.${extension}`}
          </div>
          <FormControl component="fieldset" style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Format</FormLabel>
            <RadioGroup
              row
              aria-label="format"
              name="format"
              value={options.format || FORMAT.csv}
              onChange={(e) => onChange(e.target.value, 'format')}>
              <FormControlLabel value={FORMAT.csv} control={<Radio />} label={FORMAT.csv} />
              <FormControlLabel value={FORMAT.pdf} control={<Radio />} label={FORMAT.pdf} />
            </RadioGroup>
          </FormControl>
          <div />
          <FormControl
            component="fieldset"
            style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Orientation</FormLabel>
            <RadioGroup
              row
              aria-label="orientation"
              name="orientation"
              value={options.orientation || ORIENTATION.landscape}
              onChange={(e) => onChange(e.target.value, 'orientation')}>
              <FormControlLabel
                value={ORIENTATION.portrait}
                control={<Radio />}
                label={ORIENTATION.portrait} />
              <FormControlLabel
                value={ORIENTATION.landscape}
                control={<Radio />}
                label={ORIENTATION.landscape} />
            </RadioGroup>
          </FormControl>
          <div />
          <FormControl
            component="fieldset"
            style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Delivery Type</FormLabel>
            <RadioGroup
              row
              aria-label="Delivery Type"
              name="delivery"
              value={options.delivery || DELIVERY_TYPE.download}
              onChange={(e) => onChange(e.target.value, 'delivery')}>
              <FormControlLabel
                value={DELIVERY_TYPE.download}
                control={<Radio />}
                label="Download" />
              <FormControlLabel
                value={DELIVERY_TYPE.email}
                control={<Radio />}
                label="By Email" />
            </RadioGroup>
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          {t(lang, 'misc.cancel')}
        </Button>
        <Button onClick={handleDownload} color="primary">
          {t(lang, 'misc.download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardDownloadDialog;
